import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { FeatureFlagsService } from './feature-flags.service';
import { filter, Subject, takeUntil } from 'rxjs';
import { NGXLogger } from 'ngx-logger';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagContextService implements OnDestroy {
  private router = inject(Router);
  private featureFlagService = inject(FeatureFlagsService);
  private logger = inject(NGXLogger);

  private destroy$ = new Subject<void>();
  private userAgent = window.navigator.userAgent.toLowerCase();

  constructor() {
    this.setupNavigationListener();
  }

  private setupNavigationListener() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateContext();
    });
  }

  private async updateContext() {
    try {
      const url = window.location.href;
      const urlObject = new URL(url);
      const searchParams = new URLSearchParams(urlObject.search);

      const context = {
        url: url,
        path: urlObject.pathname,
        host: urlObject.host,
        query: urlObject.search,
        deviceType: this.getDeviceType(),
        browser: this.getBrowser(),
        utmSource: searchParams.get('utm_source') || '',
        utmMedium: searchParams.get('utm_medium') || '',
        utmCampaign: searchParams.get('utm_campaign') || '',
        utmTerm: searchParams.get('utm_term') || '',
        utmContent: searchParams.get('utm_content') || ''
      };

      return this.featureFlagService.setContext(context);
    } catch (error) {
      this.logger.error('Error updating feature flag context:', error);
    }
  }

  private getDeviceType(): 'desktop' | 'mobile' {
    // Using window.innerWidth for a simple check
    // You might want to use a more sophisticated detection method
    return window.innerWidth <= 768 ? 'mobile' : 'desktop';
  }

  private getBrowser(): 'chrome' | 'edge' | 'firefox' | 'safari' | 'unknown' {
    if (this.userAgent.includes('chrome')) {
      return 'chrome';
    } else if (this.userAgent.includes('edg')) {
      return 'edge';
    } else if (this.userAgent.includes('firefox')) {
      return 'firefox';
    } else if (this.userAgent.includes('safari') && !this.userAgent.includes('chrome')) {
      return 'safari';
    }
    return 'unknown';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
